import type { ApiError } from '@shared/contracts/mockApi';

/**
 * Canonical error codes.
 *
 * The eight codes required by architecture/CUSTOMISATION_MOCK_API_CONTRACT_V5.md
 * plus the general banking codes the workflows need. Every adapter failure uses
 * this vocabulary — a locally invented code is a defect.
 */
export const ERROR_CODES = {
  VALIDATION_ERROR: 'VALIDATION_ERROR',
  PERMISSION_DENIED: 'PERMISSION_DENIED',
  FEATURE_DISABLED: 'FEATURE_DISABLED',
  VERSION_CONFLICT: 'VERSION_CONFLICT',
  POLICY_VIOLATION: 'POLICY_VIOLATION',
  WIDGET_UNAVAILABLE: 'WIDGET_UNAVAILABLE',
  AI_UNAVAILABLE: 'AI_UNAVAILABLE',
  MIGRATION_REQUIRED: 'MIGRATION_REQUIRED',

  NOT_FOUND: 'NOT_FOUND',
  PROVIDER_UNAVAILABLE: 'PROVIDER_UNAVAILABLE',
  TIMEOUT: 'TIMEOUT',
  INSUFFICIENT_FUNDS: 'INSUFFICIENT_FUNDS',
  ACCOUNT_RESTRICTED: 'ACCOUNT_RESTRICTED',
  DUPLICATE_SUBMISSION: 'DUPLICATE_SUBMISSION',
  SCA_REQUIRED: 'SCA_REQUIRED',
  SCA_FAILED: 'SCA_FAILED',
  APPROVAL_REQUIRED: 'APPROVAL_REQUIRED',
  ALREADY_ACTIONED: 'ALREADY_ACTIONED',
  SESSION_EXPIRED: 'SESSION_EXPIRED',
  OFFLINE: 'OFFLINE',
} as const;

export type ApiErrorCode = (typeof ERROR_CODES)[keyof typeof ERROR_CODES];

export class MockApiError extends Error {
  readonly code: ApiErrorCode;
  readonly fieldErrors: Record<string, string> | undefined;
  /** True when a retry could plausibly succeed, so the UI offers Try again. */
  readonly retryable: boolean;

  constructor(
    code: ApiErrorCode,
    message: string,
    options?: { fieldErrors?: Record<string, string>; retryable?: boolean },
  ) {
    super(message);
    this.name = 'MockApiError';
    this.code = code;
    this.fieldErrors = options?.fieldErrors;
    this.retryable = options?.retryable ?? RETRYABLE.has(code);
  }

  toApiError(): ApiError {
    return {
      code: this.code,
      message: this.message,
      ...(this.fieldErrors ? { fieldErrors: this.fieldErrors } : {}),
    };
  }
}

const RETRYABLE = new Set<string>([
  ERROR_CODES.PROVIDER_UNAVAILABLE,
  ERROR_CODES.TIMEOUT,
  ERROR_CODES.AI_UNAVAILABLE,
  ERROR_CODES.OFFLINE,
]);

export function isMockApiError(e: unknown): e is MockApiError {
  return e instanceof MockApiError;
}

/** Plain-language message for any thrown value, never a raw stack. */
export function errorMessage(e: unknown): string {
  if (isMockApiError(e)) return e.message;
  if (e instanceof Error && e.message) return e.message;
  return 'Something went wrong. Please try again.';
}

export function errorCode(e: unknown): ApiErrorCode | 'UNKNOWN' {
  return isMockApiError(e) ? e.code : 'UNKNOWN';
}

export function isRetryable(e: unknown): boolean {
  return isMockApiError(e) ? e.retryable : true;
}
